import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useLocation } from '../contexts/LocationContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { useBulkApproval } from '../contexts/BulkApprovalContext';
import PurchaseInvoiceCorrectionModal from '../components/PurchaseInvoiceCorrectionModal';
import api from '../services/api';
import {
  FaSearch,
  FaEye, 
  FaTrash, 
  FaCheck, 
  FaTimes,
  FaFileInvoice,
  FaArrowLeft,
  FaChevronLeft,
  FaChevronRight,
  FaCheckSquare,
  FaFilePdf,
  FaFileCode,
  FaEdit,
  FaEllipsisV
} from 'react-icons/fa';

const PurchaseInvoicesListPage = () => {
  // Pobierz lokalizację z globalnego kontekstu
  const { selectedLocation, locationId } = useLocation();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Masowe zatwierdzanie (generowanie PZ) — stan i petla zyja w globalnym
  // BulkApprovalContext (przetrwaja wyjscie ze strony); pasek postepu renderuje
  // Layout globalnie. Tu tylko odpalamy operacje i reagujemy na jej zakonczenie.
  const { running: bulkApproving, completedTick, startBulkApproval } = useBulkApproval();

  // Guard sekwencji odpowiedzi + anulowanie w locie
  const loadSeqRef = useRef(0);
  
  // Stan dla modalu korekty
  const [correctionModal, setCorrectionModal] = useState({ isOpen: false, invoice: null });
  
  // Pobierz ID lokalizacji z kontekstu
  const currentLocationId = locationId || selectedLocation?.id || selectedLocation?.location_id;
  
  // Kontrola dostawy — raporty
  const [invoicesWithReports, setInvoicesWithReports] = useState(new Set());
  const [deliveryReportModal, setDeliveryReportModal] = useState(null);

  const loadReportStatus = async () => {
    try {
      const res = await api.get('/collector/invoices-with-reports');
      setInvoicesWithReports(new Set(res.data?.data || []));
    } catch (e) { /* ignore */ }
  };

  const handleViewDeliveryReport = async (invoiceId) => {
    try {
      const res = await api.get(`/collector/delivery-reports/${invoiceId}`);
      const reports = res.data?.data || [];
      if (reports.length > 0) {
        // Pobierz szczegóły ostatniego raportu
        const detailRes = await api.get(`/collector/delivery-report/${reports[0].id}`);
        if (detailRes.data?.success) {
          setDeliveryReportModal(detailRes.data.data);
        }
      }
    } catch (e) {
      console.error('Błąd pobierania raportu:', e);
    }
  };

  const [filters, setFilters] = useState({
    search: '',
    date_from: '',
    date_to: '',
    status: '',
    supplier: ''
  });
  
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    pages: 0,
    has_prev: false,
    has_next: false
  });

  const loadInvoices = useCallback(async (signal) => {
    // signal moze byc AbortSignal (z efektu) albo brak (reczne odswiezenie).
    const abortSignal = signal instanceof AbortSignal ? signal : undefined;
    const seq = ++loadSeqRef.current;
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: pagination.page,
        per_page: pagination.per_page,
        ...filters
      });

      // Dodaj location_id do filtrowania po lokalizacji
      if (currentLocationId) {
        params.append('location_id', currentLocationId);
      }

      const response = await api.get(`/purchase-invoices?${params}`, abortSignal ? { signal: abortSignal } : {});
      // Ignoruj przestarzala odpowiedz (wyscig) — liczy sie tylko najnowsze zapytanie
      if (seq !== loadSeqRef.current) return;
      const data = response.data;

      if (data.success) {
        setInvoices(data.data.invoices);
        setPagination(prev => ({
          ...prev,
          ...data.data.pagination
        }));
      } else {
        setError(data.message);
      }
    } catch (err) {
      // Anulowane zadanie (nowszy filtr) — cicho pomijamy
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || abortSignal?.aborted) return;
      if (seq !== loadSeqRef.current) return;
      // Interceptor api.js wklada komunikat backendu do error.message
      setError(err?.message || 'Błąd połączenia z serwerem');
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [filters, pagination.page, pagination.per_page, currentLocationId]);

  // Debounce + AbortController: filtrowanie na zywo nie strzela zapytaniem
  // przy kazdym znaku i nie pozwala starszej odpowiedzi nadpisac nowszej.
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      loadInvoices(controller.signal);
      loadReportStatus();
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // loadInvoices jest memoizowane po filtrach/stronie/lokalizacji
  }, [loadInvoices]);

  // Uzgodnij zaznaczenie z aktualnie widocznymi fakturami — po zmianie
  // strony/filtra/lokalizacji nie trzymaj id ktorych juz nie ma na liscie.
  useEffect(() => {
    setSelectedInvoices(prev => prev.filter(id => invoices.some(inv => inv.id === id)));
  }, [invoices]);

  useEffect(() => {
    const handleClickOutside = () => setOpenActionMenu(null);
    if (openActionMenu !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openActionMenu]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadInvoices();
  };

  const clearFilters = () => {
    setFilters({ search: '', date_from: '', date_to: '', status: '', supplier: '' });
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Funkcja otwierania modalu korekty
  const handleOpenCorrectionModal = (invoice) => {
    setCorrectionModal({ isOpen: true, invoice });
    setOpenActionMenu(null);
  };

  // Funkcja zamknięcia modalu korekty
  const handleCloseCorrectionModal = () => {
    setCorrectionModal({ isOpen: false, invoice: null });
  };

  // Callback po zakończeniu korekty
  const handleCorrectionComplete = () => {
    loadInvoices(); // Odśwież listę faktur
  };

  const toggleInvoiceSelection = (invoiceId) => {
    setSelectedInvoices(prev => 
      prev.includes(invoiceId) 
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  // Czy wszystkie WIDOCZNE faktury sa zaznaczone (porownanie po id, nie po dlugosci)
  const allVisibleSelected = invoices.length > 0 &&
    invoices.every(inv => selectedInvoices.includes(inv.id));

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(invoices.map(inv => inv.id));
    }
  };

  // Masowe zatwierdzanie = sekwencyjne generowanie PZ dla kazdej zaznaczonej faktury.
  // SEKWENCYJNIE (nie Promise.all) — rownolegle powoduje duplikaty PZ i deadlocki na bazie.
  const handleBulkApprove = () => {
    if (bulkApproving || selectedInvoices.length === 0) return;
    if (!currentLocationId) {
      toast.error('Brak wybranej lokalizacji — nie mozna wygenerowac PZ');
      return;
    }
    // Zbuduj liste {id, numer} dla czytelnego paska postepu
    const items = selectedInvoices.map((id) => {
      const inv = invoices.find((x) => x.id === id);
      return { id, numer: inv?.numer_faktury || `#${id}` };
    });
    setError(null);
    setSelectedInvoices([]);
    // Operacja + pasek postepu zyja w globalnym kontekscie (przetrwaja
    // wyjscie ze strony). Liste odswiezymy po zakonczeniu (completedTick).
    startBulkApproval(items, currentLocationId);
  };

  // Po zakonczeniu masowego zatwierdzania (globalny kontekst) — odswiez liste.
  useEffect(() => {
    if (completedTick > 0) {
      loadInvoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedTick]);

  const handleDeleteInvoice = async (invoice) => {
    try {
      const checkResponse = await api.get(`/purchase-invoices/${invoice.id}/can-delete`);
      const checkData = checkResponse.data;
      
      if (!checkData.success) {
        setError(checkData.message);
        return;
      }
      
      if (!checkData.data.can_delete) {
        setError(`Nie można usunąć faktury ${invoice.numer_faktury} - ma powiązane dokumenty PZ`);
        setOpenActionMenu(null);
        return;
      }
      
      setDeleteConfirm(invoice);
      setOpenActionMenu(null);
    } catch (err) {
      setError(err?.message || 'Błąd sprawdzania możliwości usunięcia');
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      const response = await api.delete(`/purchase-invoices/${deleteConfirm.id}`);
      const data = response.data;
      
      if (data.success) {
        setInvoices(prev => prev.filter(inv => inv.id !== deleteConfirm.id));
        setDeleteConfirm(null);
        loadInvoices();
      } else {
        setError(data.message);
        setDeleteConfirm(null);
      }
    } catch (err) {
      setError(err?.message || 'Błąd usuwania faktury');
      setDeleteConfirm(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'nowa': { bg: 'var(--info-light)', color: 'var(--info)', text: 'Nowa' },
      'oczekujaca': { bg: 'var(--warning-light)', color: 'var(--warning)', text: 'Oczekująca' },
      'weryfikacja': { bg: 'var(--info-light)', color: 'var(--info)', text: 'Weryfikacja' },
      'zatwierdzona': { bg: 'var(--success-light)', color: 'var(--success)', text: 'Zatwierdzona' },
      'dodana': { bg: '#e8f5e9', color: '#2e7d32', text: 'Dodana (PZ)' },
      'odrzucona': { bg: 'var(--danger-light)', color: 'var(--danger)', text: 'Odrzucona' }
    };
    
    const config = statusConfig[status] || { bg: 'var(--border)', color: 'var(--text)', text: status || 'Brak' };
    
    return (
      <span style={{
        padding: '0.15rem 0.4rem',
        backgroundColor: config.bg,
        color: config.color,
        borderRadius: '3px',
        fontSize: '9px',
        fontWeight: '500'
      }}>
        {config.text}
      </span>
    );
  };

  const getImportTypeBadge = (invoice) => {
    if (invoice.zaimportowana_xml) {
      return <FaFileCode style={{ color: 'var(--success)', marginRight: '4px' }} title="XML" />;
    }
    if (invoice.zaimportowana_pdf) {
      return <FaFilePdf style={{ color: 'var(--danger)', marginRight: '4px' }} title="PDF" />;
    }
    return <FaEdit style={{ color: 'var(--text-muted)', marginRight: '4px' }} title="Ręczna" />;
  };

  if (loading && invoices.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', backgroundColor: 'var(--bg)', minHeight: '100vh' }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
        <div style={{ color: 'var(--text-muted)' }}>Ładowanie faktur...</div>
      </div>
    );
  }

  return (
    <div style={{ fontSize: '12px' }}>
      {/* Pasek postepu masowego zatwierdzania renderuje globalnie Layout
          (BulkApprovalBanner) — widoczny na kazdej podstronie i przetrwa
          wyjscie z tej listy. */}

      {/* Header */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>
              Lista Faktur Zakupowych
              {selectedLocation && (
                <span style={{
                  marginLeft: '0.5rem',
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'var(--text-muted)'
                }}>
                  {selectedLocation.nazwa || selectedLocation.name}
                </span>
              )}
            </h2>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              Przegladaj i zarzadzaj fakturami | Razem: {pagination.total}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="btn btn-secondary"
              onClick={() => navigate('/purchase-invoices')}
            >
              <FaArrowLeft style={{ fontSize: '10px', marginRight: '4px' }} />
              Menu
            </button>
            <button
              className="btn btn-primary"
              onClick={() => loadInvoices()}
              disabled={loading}
            >
              {loading ? 'Laduje...' : 'Odswiez'}
            </button>
          </div>
        </div>
      </div>

      {/* Alert błędów */}
      {error && (
        <div style={{
          padding: '0.5rem 0.75rem', 
          backgroundColor: 'var(--danger-light)', 
          color: 'var(--danger)',
          borderRadius: '4px', 
          marginBottom: '0.5rem', 
          fontSize: '11px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <span>❌ {error}</span>
          <button 
            onClick={() => setError(null)}
            style={{ background: 'none',  cursor: 'pointer', fontSize: '14px', color: 'var(--danger)' }}
          >×</button>
        </div>
      )}

      {/* Modal potwierdzenia usunięcia */}
      {deleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--card-bg)',
            padding: '1.5rem',
            borderRadius: '8px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}>
            <h5 style={{ marginBottom: '1rem', color: 'var(--danger)' }}>⚠️ Potwierdź usunięcie</h5>
            <p style={{ marginBottom: '1rem', fontSize: '12px' }}>
              Czy na pewno chcesz usunąć fakturę <strong>{deleteConfirm.numer_faktury}</strong>?
              <br/>Ta operacja jest nieodwracalna.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button 
                onClick={() => setDeleteConfirm(null)}
                style={{
                  padding: '6px 12px',
                  border: '1px solid var(--text-muted)',
                  backgroundColor: 'var(--bg)',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Anuluj
              </button>
              <button 
                onClick={confirmDelete}
                style={{
                  padding: '6px 12px',
                  border: '1px solid var(--danger)', color: 'var(--danger)' ,
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Usuń
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filtry */}
      <div style={{
        backgroundColor: 'var(--card-bg)', 
        borderRadius: '4px', 
        boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
        marginBottom: '0.5rem',
        overflow: 'hidden'
      }}>
        <div style={{ 
          padding: '0.5rem 0.75rem', 
          borderBottom: '1px solid var(--border)', 
          fontWeight: '600', 
          fontSize: '12px', 
          backgroundColor: 'var(--bg)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>🔍 Wyszukiwanie i filtry</span>
          {(filters.search || filters.date_from || filters.date_to || filters.status) && 
            <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Wyników: <strong>{invoices.length}</strong></span>
          }
        </div>
        <div style={{ padding: '0.5rem' }}>
          <form onSubmit={handleSearch}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
              gap: '0.5rem', 
              marginBottom: '0.5rem'
            }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)' }}>
                  Numer / Dostawca
                </label>
                <input 
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Szukaj..."
                  style={{
                    width: '100%',
                    padding: '6px 10px',
                    fontSize: '12px',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    backgroundColor: 'var(--card-bg)',
                    color: 'var(--text)',
                    outline: 'none'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)' }}>
                  Data od
                </label>
                <input 
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => handleFilterChange('date_from', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '6px 10px',
                    fontSize: '12px',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    backgroundColor: 'var(--card-bg)',
                    color: 'var(--text)',
                    outline: 'none'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)' }}>
                  Data do
                </label>
                <input 
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => handleFilterChange('date_to', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '6px 10px',
                    fontSize: '12px',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    backgroundColor: 'var(--card-bg)',
                    color: 'var(--text)',
                    outline: 'none'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)' }}>
                  Status
                </label>
                <select 
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '6px 10px',
                    fontSize: '12px',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    outline: 'none',
                    backgroundColor: 'var(--card-bg)',
                    color: 'var(--text)'
                  }}
                >
                  <option value="">Wszystkie</option>
                  <option value="nowa">Nowa</option>
                  <option value="oczekujaca">Oczekująca</option>
                  <option value="weryfikacja">Weryfikacja</option>
                  <option value="zatwierdzona">Zatwierdzona</option>
                  <option value="dodana">Dodana (PZ)</option>
                  <option value="odrzucona">Odrzucona</option>
                </select>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button 
                type="submit" 
                style={{
                  padding: '4px 10px',
                  fontSize: '11px',
                  border: '1px solid var(--primary)', color: 'var(--primary)' ,
                  cursor: 'pointer',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <FaSearch style={{ fontSize: '9px' }} />
                Szukaj
              </button>
              <button 
                type="button" 
                onClick={clearFilters}
                style={{
                  padding: '4px 10px',
                  fontSize: '11px',
                  border: '1px solid var(--text-muted)',
                  backgroundColor: 'var(--bg)',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <FaTimes style={{ fontSize: '9px' }} />
                Wyczyść
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Lista faktur */}
      <div style={{
        backgroundColor: 'var(--card-bg)', 
        borderRadius: '4px', 
        boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
        overflow: 'hidden'
      }}>
        <div style={{ 
          padding: '0.5rem 0.75rem', 
          borderBottom: '1px solid var(--border)', 
          fontWeight: '600', 
          fontSize: '12px', 
          backgroundColor: 'var(--bg)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>📋 Faktury ({pagination.total})</span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button 
              onClick={toggleSelectAll}
              style={{
                padding: '4px 10px',
                fontSize: '11px',
                border: '1px solid var(--info)',
                backgroundColor: allVisibleSelected ? 'var(--primary)' : 'var(--card-bg)' ,
                color: allVisibleSelected ? '#fff' : 'var(--primary)',
                cursor: 'pointer',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '3px'
              }}
            >
              <FaCheckSquare style={{ fontSize: '9px' }} />
              {allVisibleSelected ? 'Odznacz' : 'Zaznacz'}
            </button>
            <button
              onClick={handleBulkApprove}
              disabled={selectedInvoices.length === 0 || bulkApproving}
              title="Zatwierdz zaznaczone faktury (wygeneruj PZ)"
              style={{
                padding: '4px 10px',
                fontSize: '11px',
                border: (selectedInvoices.length === 0 || bulkApproving) ? '1px solid var(--text-muted)' : '1px solid var(--success)',
                backgroundColor: (selectedInvoices.length === 0 || bulkApproving) ? 'var(--bg)' : 'var(--success)',
                color: (selectedInvoices.length === 0 || bulkApproving) ? 'var(--text-muted)' : '#fff' ,
                cursor: (selectedInvoices.length === 0 || bulkApproving) ? 'not-allowed' : 'pointer',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '3px'
              }}
            >
              <FaCheck style={{ fontSize: '9px' }} />
              {bulkApproving
                ? 'Zatwierdzanie...'
                : `Zatwierdź (${selectedInvoices.length})`}
            </button>
          </div>
        </div>
          
        <div style={{ padding: invoices.length === 0 ? '1rem' : '0' }}>
          {invoices.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '2rem' }}>
              <FaFileInvoice style={{ fontSize: '2rem', marginBottom: '0.5rem', opacity: 0.3 }} />
              <p style={{ margin: 0, fontSize: '11px' }}>Brak faktur spełniających kryteria</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                    <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: '600', color: 'var(--text-secondary)', width: '30px' }}>
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={toggleSelectAll}
                        style={{ cursor: 'pointer' }}
                      />
                    </th>
                    <th style={{ padding: '0.4rem', textAlign: 'center', fontWeight: '600', color: 'var(--text-secondary)', width: '40px' }}>LP</th>
                    <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: '600', color: 'var(--text-secondary)', minWidth: '180px', maxWidth: '220px' }}>Numer faktury</th>
                    <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: '600', color: 'var(--text-secondary)', minWidth: '85px', whiteSpace: 'nowrap' }}>Data</th>
                    <th style={{ padding: '0.4rem', textAlign: 'left', fontWeight: '600', color: 'var(--text-secondary)' }}>Dostawca</th>
                    <th style={{ padding: '0.4rem', textAlign: 'right', fontWeight: '600', color: 'var(--text-secondary)', minWidth: '80px' }}>Netto</th>
                    <th style={{ padding: '0.4rem', textAlign: 'right', fontWeight: '600', color: 'var(--text-secondary)', minWidth: '80px' }}>Brutto</th>
                    <th style={{ padding: '0.4rem', textAlign: 'center', fontWeight: '600', color: 'var(--text-secondary)' }}>Status</th>
                    <th style={{ padding: '0.4rem', textAlign: 'center', fontWeight: '600', color: 'var(--text-secondary)' }}>Poz.</th>
                    <th style={{ padding: '0.4rem', textAlign: 'center', fontWeight: '600', color: 'var(--text-secondary)', width: '30px' }} title="Kontrola dostawy">KD</th>
                    <th style={{ padding: '0.4rem', textAlign: 'center', fontWeight: '600', color: 'var(--text-secondary)' }}>Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice, index) => (
                    <tr 
                      key={invoice.id} 
                      style={{ 
                        backgroundColor: index % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)',
                        borderBottom: '1px solid var(--border)',
                        cursor: 'pointer'
                      }}
                      onClick={() => navigate(`/purchase-invoices/${invoice.id}`)}
                    >
                      <td style={{ padding: '0.4rem' }} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedInvoices.includes(invoice.id)}
                          onChange={() => toggleInvoiceSelection(invoice.id)}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                      <td style={{ padding: '0.4rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '10px' }}>
                        {((pagination.page - 1) * pagination.per_page) + index + 1}
                      </td>
                      <td style={{ padding: '0.4rem', minWidth: '180px', maxWidth: '220px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                          {getImportTypeBadge(invoice)}
                          <span style={{ fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{invoice.numer_faktury || `FZ-${invoice.id}`}</span>
                        </div>
                      </td>
                      <td style={{ padding: '0.4rem', minWidth: '85px', whiteSpace: 'nowrap' }}>
                        {invoice.data_faktury ? new Date(invoice.data_faktury).toLocaleDateString('pl-PL') : '-'}
                      </td>
                      <td style={{ padding: '0.4rem', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {invoice.nazwa_dostawcy || invoice.dostawca_nazwa || 'Nieznany'}
                      </td>
                      <td style={{ padding: '0.4rem', textAlign: 'right', fontWeight: '500' }}>
                        {parseFloat(invoice.suma_netto || 0).toFixed(2)} zł
                      </td>
                      <td style={{ padding: '0.4rem', textAlign: 'right', fontWeight: '600', color: 'var(--success)' }}>
                        {parseFloat(invoice.suma_brutto || 0).toFixed(2)} zł
                      </td>
                      <td style={{ padding: '0.4rem', textAlign: 'center' }}>
                        {getStatusBadge(invoice.status)}
                      </td>
                      <td style={{ padding: '0.4rem', textAlign: 'center' }}>
                        <span style={{ 
                          backgroundColor: 'var(--info-light)', 
                          color: 'var(--info)', 
                          padding: '0.15rem 0.4rem', 
                          borderRadius: '3px',
                          fontSize: '10px',
                          fontWeight: '500'
                        }}>
                          {invoice.pozycje_count || 0}
                        </span>
                      </td>
                      <td style={{ padding: '0.4rem', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        {invoicesWithReports.has(invoice.id) ? (
                          <span
                            title="Kontrola dostawy wykonana — kliknij aby zobaczyć"
                            onClick={() => handleViewDeliveryReport(invoice.id)}
                            style={{ cursor: 'pointer', fontSize: '14px' }}
                          >✅</span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontSize: '10px' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '0.4rem', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ position: 'relative' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenActionMenu(openActionMenu === invoice.id ? null : invoice.id);
                            }}
                            style={{
                              padding: '4px 10px',
                              border: '1px solid var(--border)',
                              backgroundColor: 'var(--card-bg)',
                              cursor: 'pointer',
                              fontSize: '11px'
                            }}
                          >
                            <FaEllipsisV />
                          </button>
                          
                          {openActionMenu === invoice.id && (
                            <div style={{
                              position: 'absolute',
                              right: 0,
                              top: '100%',
                              backgroundColor: 'var(--card-bg)',
                              border: '1px solid var(--border)',
                              borderRadius: '4px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              zIndex: 100,
                              minWidth: '140px'
                            }}>
                              <button
                                onClick={() => navigate(`/purchase-invoices/${invoice.id}`)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  padding: '6px 12px',
                                  width: '100%',
                                  
                                  backgroundColor: 'transparent',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  textAlign: 'left'
                                }}
                              >
                                <FaEye style={{ color: 'var(--primary)' }} /> Szczegóły
                              </button>
                              <button
                                onClick={() => handleOpenCorrectionModal(invoice)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  padding: '6px 12px',
                                  width: '100%',
                                  
                                  backgroundColor: 'transparent',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  textAlign: 'left',
                                  color: 'var(--warning)'
                                }}
                              >
                                <FaEdit style={{ color: 'var(--warning)' }} /> Korekta
                              </button>
                              <button
                                onClick={() => handleDeleteInvoice(invoice)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  padding: '6px 12px',
                                  width: '100%',
                                  
                                  backgroundColor: 'transparent',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  textAlign: 'left',
                                  color: 'var(--danger)'
                                }}
                              >
                                <FaTrash /> Usuń
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Paginacja - zawsze widoczna */}
        <div style={{
          padding: '0.75rem',
          borderTop: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            {/* Wybór liczby elementów na stronie */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '1rem' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Pokaż:</span>
              <select
                value={pagination.per_page}
                onChange={(e) => {
                  const newPerPage = parseInt(e.target.value);
                  setPagination(prev => ({ ...prev, per_page: newPerPage, page: 1 }));
                }}
                style={{
                  padding: '6px 10px',
                  fontSize: '12px',
                  border: '1px solid var(--border)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--card-bg)',
                  color: 'var(--text)'
                }}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="30">30</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="500">500</option>
                <option value="1000">1000</option>
              </select>
            </div>

            {/* Przycisk pierwsza strona */}
            <button
              onClick={() => handlePageChange(1)}
              disabled={pagination.page === 1}
              style={{
                padding: '4px 10px',
                fontSize: '11px',
                border: '1px solid var(--border)',
                backgroundColor: pagination.page === 1 ? 'var(--bg)' : 'var(--card-bg)' ,
                color: pagination.page === 1 ? 'var(--text-muted)' : 'var(--text-secondary)',
                cursor: pagination.page === 1 ? 'not-allowed' : 'pointer'
              }}
              title="Pierwsza strona"
            >
              «
            </button>

            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={!pagination.has_prev}
              style={{
                padding: '4px 10px',
                fontSize: '11px',
                border: '1px solid var(--border)',
                backgroundColor: !pagination.has_prev ? 'var(--bg)' : 'var(--card-bg)' ,
                color: !pagination.has_prev ? 'var(--text-muted)' : 'var(--text-secondary)',
                cursor: !pagination.has_prev ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <FaChevronLeft style={{ fontSize: '9px' }} />
              Poprzednia
            </button>

            {/* Numery stron */}
            {pagination.pages > 0 && (
              <div style={{ display: 'flex', gap: '2px' }}>
                {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                  let pageNum;
                  if (pagination.pages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.page >= pagination.pages - 2) {
                    pageNum = pagination.pages - 4 + i;
                  } else {
                    pageNum = pagination.page - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      style={{
                        padding: '4px 10px',
                        fontSize: '11px',
                        border: '1px solid var(--border)',
                        backgroundColor: pagination.page === pageNum ? 'var(--primary)' : 'var(--card-bg)' ,
                        color: pagination.page === pageNum ? '#fff' : 'var(--text-secondary)',
                        cursor: 'pointer',
                        minWidth: '28px',
                        fontWeight: pagination.page === pageNum ? '600' : '400'
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
            )}

            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={!pagination.has_next}
              style={{
                padding: '4px 10px',
                fontSize: '11px',
                border: '1px solid var(--border)',
                backgroundColor: !pagination.has_next ? 'var(--bg)' : 'var(--card-bg)' ,
                color: !pagination.has_next ? 'var(--text-muted)' : 'var(--text-secondary)',
                cursor: !pagination.has_next ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              Następna
              <FaChevronRight style={{ fontSize: '9px' }} />
            </button>

            {/* Przycisk ostatnia strona */}
            <button
              onClick={() => handlePageChange(pagination.pages)}
              disabled={pagination.page === pagination.pages || pagination.pages === 0}
              style={{
                padding: '4px 10px',
                fontSize: '11px',
                border: '1px solid var(--border)',
                backgroundColor: pagination.page === pagination.pages ? 'var(--bg)' : 'var(--card-bg)' ,
                color: pagination.page === pagination.pages ? 'var(--text-muted)' : 'var(--text-secondary)',
                cursor: pagination.page === pagination.pages ? 'not-allowed' : 'pointer'
              }}
              title="Ostatnia strona"
            >
              »
            </button>
          </div>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
            Pokazuję {((pagination.page - 1) * pagination.per_page) + 1} - {Math.min(pagination.page * pagination.per_page, pagination.total)} z {pagination.total} faktur
          </div>
        </div>
      </div>

      {/* Modal korekty faktury zakupowej */}
      <PurchaseInvoiceCorrectionModal
        isOpen={correctionModal.isOpen}
        onClose={handleCloseCorrectionModal}
        invoice={correctionModal.invoice}
        onCorrectionComplete={handleCorrectionComplete}
      />

      {/* Modal raportu kontroli dostawy */}
      {deliveryReportModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000,
          display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
          padding: '2rem', overflowY: 'auto'
        }}>
          <div style={{
            backgroundColor: 'var(--card-bg)', borderRadius: '8px',
            width: '100%', maxWidth: '700px', padding: '1.5rem'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700' }}>
                Raport kontroli dostawy
              </h5>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={() => {
                  const url = `/api/collector/delivery-report/${deliveryReportModal.id}/pdf`;
                  window.open(url, '_blank');
                }} style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--success)', color: 'var(--success)', cursor: 'pointer' }}>
                  <i className="fas fa-file-pdf" style={{ marginRight: '4px' }}></i>PDF
                </button>
                <button onClick={() => window.print()} style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--text-muted)', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                  <i className="fas fa-print" style={{ marginRight: '4px' }}></i>Drukuj
                </button>
                <button onClick={() => setDeliveryReportModal(null)} style={{ background: 'none', fontSize: '18px', cursor: 'pointer', color: 'var(--text-muted)' }}>×</button>
              </div>
            </div>

            {/* Info o fakturze */}
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '0.75rem', padding: '0.5rem', backgroundColor: 'var(--bg)', borderRadius: '4px' }}>
              <strong>{deliveryReportModal.numer_faktury}</strong> | {deliveryReportModal.dostawca_nazwa} | {deliveryReportModal.data_faktury}
              <br />
              <span style={{ fontSize: '10px' }}>Kontrolę wykonał: <strong>{deliveryReportModal.created_by}</strong> | {deliveryReportModal.created_at ? new Date(deliveryReportModal.created_at).toLocaleString('pl-PL') : ''}</span>
            </div>

            {/* Podsumowanie */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', marginBottom: '0.75rem' }}>
              {[
                { label: 'Zgodne', value: deliveryReportModal.matched || 0, color: 'var(--success)' },
                { label: 'Braki', value: deliveryReportModal.missing || 0, color: 'var(--danger)' },
                { label: 'Nadwyżki', value: deliveryReportModal.excess || 0, color: 'var(--warning)' },
                { label: 'Spoza fakt.', value: deliveryReportModal.not_on_invoice || 0, color: 'var(--info)' },
              ].map(s => (
                <div key={s.label} style={{ textAlign: 'center', padding: '0.5rem', backgroundColor: 'var(--bg)', borderRadius: '4px', borderLeft: `3px solid ${s.color}` }}>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Tabela pozycji */}
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--bg)' }}>
                  <tr>
                    {['LP', 'Produkt', 'EAN', 'Faktura', 'Skan', 'Różnica', 'Status'].map(h => (
                      <th key={h} style={{ padding: '0.4rem', borderBottom: '2px solid var(--border)', textAlign: h === 'LP' ? 'center' : 'left', fontWeight: '600', fontSize: '10px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(deliveryReportModal.report_data || []).map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '0.3rem 0.4rem', textAlign: 'center', color: 'var(--text-muted)' }}>{idx + 1}</td>
                      <td style={{ padding: '0.3rem 0.4rem', fontWeight: '500' }}>{item.nazwa}</td>
                      <td style={{ padding: '0.3rem 0.4rem', fontSize: '10px', color: 'var(--text-muted)' }}>{item.ean || '-'}</td>
                      <td style={{ padding: '0.3rem 0.4rem' }}>{item.expected_qty}</td>
                      <td style={{ padding: '0.3rem 0.4rem' }}>{item.scanned_qty}</td>
                      <td style={{ padding: '0.3rem 0.4rem' }}>
                        <span style={{ fontWeight: '700', color: item.difference === 0 ? 'var(--success)' : item.difference > 0 ? 'var(--warning)' : 'var(--danger)' }}>
                          {item.difference > 0 ? '+' : ''}{item.difference}
                        </span>
                      </td>
                      <td style={{ padding: '0.3rem 0.4rem' }}>
                        <span style={{
                          fontSize: '9px', padding: '2px 6px', borderRadius: '3px', color: '#fff', fontWeight: '600',
                          backgroundColor: item.status === 'ok' ? 'var(--success)' : item.status === 'missing' || item.status === 'not_scanned' ? 'var(--danger)' : item.status === 'excess' ? 'var(--warning)' : 'var(--info)'
                        }}>
                          {item.status === 'ok' ? 'OK' : item.status === 'missing' ? 'BRAK' : item.status === 'not_scanned' ? 'NIE SKAN.' : item.status === 'excess' ? 'NADWYŻKA' : 'SPOZA FAKT.'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              onClick={() => setDeliveryReportModal(null)}
              style={{ marginTop: '1rem', width: '100%', padding: '6px 12px', border: '1px solid var(--text-muted)', color: 'var(--text-secondary)', fontSize: '12px', cursor: 'pointer' }}
            >
              Zamknij
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseInvoicesListPage;
